import type { OrganizationSubscription } from "@arbeidsark/shared"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { Button, OutlineButton } from "~/components"
import { Margin } from "~/ur-react-components"
import { useTranslate } from "~/ur-react-hooks"
import { format } from "~/utils/date-fns/format"
import { useOrganizationMembership } from "~/utils/hooks/useOrganizationMembership"
import { trpc } from "~/utils/trpc"

const Layout = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 6rem;
`

const Card = styled.div`
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation3};
  padding: 2rem;
  width: 456px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 356px;
`

const Title = styled.h4`
  font-size: 18px;
  color: ${(props) => props.theme.colors.gray0};
`

const Subtitle = styled.p`
  margin: 0;
`

const ActiveDescription = styled.p`
  text-align: center;
`

const Cancel = styled.button`
  ${(props) => props.theme.button.reset}
  color: ${(props) => props.theme.colors.gray60};

  &:hover {
    text-decoration: underline;
  }
`

export interface SubscriptionSchoolTrialingProps {
  schoolSubscription: OrganizationSubscription
}

const SubscriptionSchoolTrialing: React.FC<SubscriptionSchoolTrialingProps> = ({
  schoolSubscription,
}) => {
  const organizationMembership = useOrganizationMembership()!
  const trpcContext = trpc.useContext()

  const formattedTrialEndDate = format(
    schoolSubscription.trialEnd ?? new Date(),
    "d. MMMM yyyy",
  )

  const { mutate: setActivateAfterTrial, isLoading } =
    trpc.subscriptions.setOrganizationSubscriptionActivateAfterTrial.useMutation(
      {
        onSuccess() {
          trpcContext.subscriptions.getOrganizationSubscription.invalidate()
        },
      },
    )

  const translations = useTranslate({
    title: "subscription-school.trial.title",
    subtitle: [
      "subscription-school.trial.subtitle",
      { date: formattedTrialEndDate },
    ],
    activeDescription: [
      "subscription-school.trial.active-description",
      { date: formattedTrialEndDate },
    ],
    activate: "subscription-school.trial.activate",
    deactivate: "subscription-school.trial.deactivate",
  })

  return (
    <Layout>
      <Card>
        <Content>
          <Title>{translations.title}</Title>
          <Subtitle>{translations.subtitle()}</Subtitle>
          {schoolSubscription.activateAfterTrial && (
            <ActiveDescription>
              {translations.activeDescription()}
              {/* Du har valgt å aktivere abonnementet etter prøvetiden. Første
              faktura vil bli utstedt 08.03.2022. */}
            </ActiveDescription>
          )}
          <Margin top="1.5rem" bottom="1.5rem">
            {schoolSubscription.activateAfterTrial ? (
              <OutlineButton
                disabled={isLoading}
                onClick={() => {
                  setActivateAfterTrial({
                    organizationId: organizationMembership.organizationId,
                    activateAfterTrial: !schoolSubscription.activateAfterTrial,
                  })
                }}
              >
                {translations.deactivate}
              </OutlineButton>
            ) : (
              <Button
                disabled={isLoading}
                onClick={() => {
                  setActivateAfterTrial({
                    organizationId: organizationMembership.organizationId,
                    activateAfterTrial: !schoolSubscription.activateAfterTrial,
                  })
                }}
              >
                {translations.activate}
              </Button>
            )}
          </Margin>
          <Link to="/subscription/school-cancel">
            <Cancel>Avslutt abonnement</Cancel>
          </Link>
        </Content>
      </Card>
    </Layout>
  )
}

export default SubscriptionSchoolTrialing
